import { __awaiter } from "tslib";
import { makeAutoObservable, runInAction } from "mobx";
import { nanoid } from "nanoid";
import { kAnalyzerUserParams } from "../../../shared/shared";
import { IDEAS_EMPTY_MESSAGE, IDEAS_REQUEST_FAILED_MESSAGE } from "./ai-evaluation-messages";
// How long an Ideas click's own Firebase writes/reads may take before giving up on this attempt.
// Short — these are small foreground calls, not the evaluation itself (see below).
export const kIdeasRequestDeadlineMs = 15000;
export const REMOTE_COMMENT = "remote";
export const LOCAL_COMMENT = "local";
// How long a pending remote comment waits for a comment, a status, or this expiry before giving
// up. Comfortably above Shutterbug's ~45s budget plus the model call.
const kRemoteCommentExpiryMs = 120000;
/**
 * DocumentCommentsManager
 *
 * Manages comments for a document including:
 * - Fetching and monitoring comments from Firestore
 * - Managing a queue of pending comments
 * - Coordinating the order in which comments appear
 *
 */
export class DocumentCommentsManager {
    constructor() {
        Object.defineProperty(this, "comments", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "pendingComments", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "isCheckingPending", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        /** False until `setComments` has run at least once. `comments` is `[]` until then, so a status
         * message shown before the panel's first Firestore snapshot arrives would otherwise treat every
         * pre-existing analyzer comment as newly arrived — see `setComments`. */
        Object.defineProperty(this, "hasLoadedComments", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        /** An inline status line shown in place of a real AI comment: the "add some work" nudge, or a
         * failure message. Client-only, and deliberately not a `pendingComments` entry — it would
         * otherwise block an exemplar comment queued behind it. `priorAnalyzerCommentIds` is the set of
         * analyzer comment ids that already existed when the message was shown, so a newer one can be
         * detected by id rather than by comparing this client's clock against Firestore's server clock. */
        Object.defineProperty(this, "statusMessage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        /** The request id of the most recent Ideas click, or `null` if it made none (an empty-document
         * click). Not cleared when a request resolves — it tracks the latest click, not what's pending. */
        Object.defineProperty(this, "latestIdeasRequestId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        /** True from the start of an Ideas click until its pending entry is queued (or, for an
         * empty-document click, until the handler returns). */
        Object.defineProperty(this, "ideasClickInProgress", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        makeAutoObservable(this, {}, { autoBind: true });
    }
    /** Whether a new Ideas click may make a request: false while one is in progress or an AI
     * evaluation is pending. The nudge doesn't factor in — an empty click makes no request. */
    get canRequestIdeas() {
        return !this.ideasClickInProgress &&
            !this.pendingComments.some(p => p.postingType === REMOTE_COMMENT && p.source === "ai");
    }
    /**
     * Check pending comments to see if any can be resolved or posted.
     * This is called automatically when comments are updated via Firestore, but can also be called manually.
     */
    checkPendingComments() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isCheckingPending)
                return;
            this.isCheckingPending = true;
            try {
                const toRemove = [];
                const toPost = [];
                for (const pending of this.pendingComments) {
                    if (pending.postingType === REMOTE_COMMENT) {
                        if (pending.checkCompleted(this.comments)) {
                            toRemove.push(pending.id);
                        }
                    }
                    else {
                        // Other comments should be posted after all preceding pending items are resolved.
                        const indexOfThis = this.pendingComments.indexOf(pending);
                        const allBeforeAreResolved = this.pendingComments
                            .slice(0, indexOfThis)
                            .every(p => toRemove.includes(p.id));
                        if (allBeforeAreResolved) {
                            toPost.push(pending);
                            toRemove.push(pending.id);
                        }
                    }
                }
                // Remove resolved items immediately.
                this.removePendingComments(new Set(toRemove));
                // Post local comments.
                for (const commentToPost of toPost) {
                    try {
                        yield commentToPost.postFunction({
                            comment: commentToPost.comment,
                            context: commentToPost.context,
                            document: commentToPost.document
                        });
                    }
                    catch (error) {
                        console.error("Failed to post pending local comment:", error);
                    }
                }
            }
            finally {
                this.isCheckingPending = false;
            }
        });
    }
    setComments(comments) {
        // The first load establishes the baseline rather than reporting new activity: comments is []
        // until now, so a status message shown before this point would otherwise see every
        // pre-existing analyzer comment as newly arrived.
        const isFirstLoad = !this.hasLoadedComments;
        this.hasLoadedComments = true;
        this.comments = comments;
        if (this.statusMessage && !isFirstLoad) {
            const { priorAnalyzerCommentIds } = this.statusMessage;
            const hasNewAnalyzerComment = comments.some(c => c.uid === kAnalyzerUserParams.id && !priorAnalyzerCommentIds.has(c.id));
            if (hasNewAnalyzerComment)
                this.clearStatusMessage();
        }
        this.checkPendingComments();
    }
    /** Removes the given pending entries, calling `dispose` on any remote ones. The single place
     * entries leave `pendingComments`, so a listener set up for one is never left dangling. */
    removePendingComments(ids) {
        if (ids.size === 0)
            return;
        const removed = this.pendingComments.filter(p => ids.has(p.id));
        if (removed.length === 0)
            return;
        runInAction(() => {
            this.pendingComments = this.pendingComments.filter(p => !ids.has(p.id));
        });
        removed.forEach(p => {
            var _a;
            if (p.postingType === REMOTE_COMMENT)
                (_a = p.dispose) === null || _a === void 0 ? void 0 : _a.call(p);
        });
    }
    queueRemoteComment({ triggeredAt, source, checkCompleted, requestId, dispose }) {
        const id = `${source}-comment-${nanoid()}`;
        const timer = setTimeout(() => {
            this.removePendingComments(new Set([id]));
            // Nothing else resolved this entry (no comment, no status) within the expiry window. Say so
            // rather than letting the bubble vanish unexplained.
            if (requestId && requestId === this.latestIdeasRequestId) {
                this.showStatusMessage(IDEAS_REQUEST_FAILED_MESSAGE);
            }
            this.checkPendingComments();
        }, kRemoteCommentExpiryMs);
        const wrappedDispose = () => {
            clearTimeout(timer);
            dispose === null || dispose === void 0 ? void 0 : dispose();
        };
        const pending = {
            id,
            triggeredAt,
            postingType: REMOTE_COMMENT,
            source,
            checkCompleted,
            requestId,
            expiresAt: Date.now() + kRemoteCommentExpiryMs,
            dispose: wrappedDispose
        };
        this.pendingComments.push(pending);
    }
    setLatestIdeasRequestId(id) {
        this.latestIdeasRequestId = id;
    }
    setIdeasClickInProgress(inProgress) {
        this.ideasClickInProgress = inProgress;
    }
    showStatusMessage(message) {
        const priorAnalyzerCommentIds = new Set(this.comments.filter(c => c.uid === kAnalyzerUserParams.id).map(c => c.id));
        this.statusMessage = { message, priorAnalyzerCommentIds };
    }
    clearStatusMessage() {
        this.statusMessage = null;
    }
    /**
     * Applies a completion status the analysis pipeline wrote for an evaluation request. Matches by
     * `requestId` only, never `completedAt` — an older request finishing late must resolve only its
     * own entry.
     *
     * A `"commented"` status does nothing: it arrives over the Realtime Database while the comment
     * itself arrives over Firestore, with no ordering between the two, so resolving on the status
     * could let a queued exemplar comment post before Ada's own comment lands. Only the comment
     * (via `checkCompleted`) or the 120s expiry resolves a commented request.
     */
    applyEvaluationStatus(status) {
        if (!(status === null || status === void 0 ? void 0 : status.requestId))
            return;
        if (status.outcome === "commented")
            return;
        const matchingIds = new Set(this.pendingComments
            .filter((p) => p.postingType === REMOTE_COMMENT && p.requestId === status.requestId)
            .map(p => p.id));
        this.removePendingComments(matchingIds);
        // Only the latest click's status may change what the student sees — an older request
        // resolving late must not show a message over a click the student already made.
        if (status.requestId === this.latestIdeasRequestId) {
            switch (status.outcome) {
                case "skipped-empty":
                    this.showStatusMessage(IDEAS_EMPTY_MESSAGE);
                    break;
                case "failed":
                    this.showStatusMessage(IDEAS_REQUEST_FAILED_MESSAGE);
                    break;
                default: {
                    // EvaluationOutcome (shared/shared.ts) gained a value this switch doesn't handle, so
                    // this fails to compile until it's given a message — or, since this value came over the
                    // wire, another class member wrote something unexpected to the writable status node.
                    // Either way, log rather than throwing inside a Realtime Database listener callback.
                    const unhandled = status.outcome;
                    console.error(`Unhandled evaluation outcome: ${unhandled}`);
                }
            }
        }
        this.checkPendingComments();
    }
    queueComment({ comment, context, document, source, postFunction }) {
        const pending = {
            id: `local-comment-${nanoid()}`,
            comment,
            context,
            document,
            postingType: LOCAL_COMMENT,
            source,
            postFunction
        };
        this.pendingComments.push(pending);
        this.checkPendingComments();
    }
    dispose() {
        this.pendingComments.forEach(p => {
            var _a;
            if (p.postingType === REMOTE_COMMENT)
                (_a = p.dispose) === null || _a === void 0 ? void 0 : _a.call(p);
        });
        this.comments = [];
        this.pendingComments = [];
    }
}
//# sourceMappingURL=data:application/json;base64,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